
import { useState, useEffect } from "react";
import IconProvider from "@/components/ui/IconProvider";
import { GeneralSetupSection, GamesSetupSection, UserManagementSection, InvitationCodesSection, ProfileSection, EnvironmentSettingsSection, PermissionsSection, ApiKeysSection, ImportExportSection, ExportTemplatesSection, StatusManagementSection, SanityCheckSection } from "./settingspage_sections";
import TutorialManagementSection from "./settingspage_sections/TutorialManagementSection";
import DatabaseSection from "./settingspage_sections/DatabaseSection";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { hasPermission } from "@/lib/utils/permissions";
import { usePageAccessGuard } from '@/hooks/usePageAccessGuard';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

// localStorage keys for state persistence
const STORAGE_KEY_SECTION = 'locanest:settings:selectedSection';
const STORAGE_KEY_EXPANDED = 'locanest:settings:expandedSections';

export default function SettingsPage() {
  usePageAccessGuard('settings');
  const { t } = usePlatformTranslation();
  const { selectedGame, selectedCompany } = useSimplifiedUserData();
  
  // Initialize expanded sections from localStorage or defaults
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_EXPANDED);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error reading expanded sections from localStorage:', error);
    }
    return {
      account: true,
      gcmeManager: false,
      company: false,
      dataManager: false
    };
  });

  // Initialize selected section from localStorage or default
  const [selectedSection, setSelectedSection] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_SECTION);
      if (saved) {
        return saved;
      }
    } catch (error) {
      console.error('Error reading selected section from localStorage:', error);
    }
    return 'profile';
  });

  // Persist selectedSection to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SECTION, selectedSection);
    } catch (error) {
      console.error('Error saving selected section to localStorage:', error);
    }
  }, [selectedSection]);

  // Persist expandedSections to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_EXPANDED, JSON.stringify(expandedSections));
    } catch (error) {
      console.error('Error saving expanded sections to localStorage:', error);
    }
  }, [expandedSections]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Check if user can manage tutorials (only owners/admins)
  const canManageTutorials = selectedCompany ? hasPermission(selectedCompany.id, 'roles.manage') : false;

  const sidebarSections = [
    {
      id: 'account',
      title: 'Account',
      items: [
        { id: 'profile', label: 'Profile' },
        { id: 'environment-settings', label: 'Environment Settings' }
      ]
    },
    {
      id: 'gameManager',
      title: 'Games',
      items: [
        { id: 'games', label: 'Game Management' },
      ]
    },

    {
      id: 'company',
      title: 'Company',
      items: [
        { id: 'general-setup', label: 'General' },
        { id: 'user-management', label: 'User Management' },
        { id: 'invitation-codes', label: 'Invitation Codes' },
        { id: 'permissions', label: 'Permissions & Roles' },
        { id: 'status-management', label: 'Status Workflows' },

       /*  ...(canManageTutorials ? [{ id: 'tutorial', label: 'Tutorial' }] : []) */
        //{ id: 'billing', label: 'Billing' }
      ]
    },

    {
      id: 'dataManager',
      title: 'Data Manager',
      items: [
        { id: 'sanity-check', label: 'Data Integrity Check' },  

        { id: 'database', label: 'Database' },
        { id: 'import-export', label: 'Export Data' },
        { id: 'export-templates', label: 'Code Templates' },
        { id: 'api-keys', label: 'API Keys' },
      ]
    }
  ];

  // Auto-expand the parent section that contains the selected section
  useEffect(() => {
    const parentSection = sidebarSections.find(section => 
      section.items.some(item => item.id === selectedSection)
    );
    
    if (parentSection && !expandedSections[parentSection.id as keyof typeof expandedSections]) {
      setExpandedSections(prev => ({
        ...prev,
        [parentSection.id]: true
      }));
    }
  }, [selectedSection]);

  // Listen for navigate-settings events from child components
  useEffect(() => {
    const handleNavigateSettings = (event: CustomEvent<string>) => {
      const sectionId = event.detail;
      // Validate that the section exists
      const sectionExists = sidebarSections.some(section =>
        section.items.some(item => item.id === sectionId)
      );
      if (sectionExists) {
        setSelectedSection(sectionId);
      }
    };

    window.addEventListener('navigate-settings', handleNavigateSettings as EventListener);
    return () => {
      window.removeEventListener('navigate-settings', handleNavigateSettings as EventListener);
    };
  }, []);

  return (
    <>
      <div className="flex flex-1  pl-16 h-full">
        {/* Sidebar */}
        <div className="w-64  border-r border-input overflow-y-auto">
          <div className="p-4">
            {sidebarSections.map((section) => (
              <div key={section.id} className="mb-4">
                <button
                  onClick={() => toggleSection(section.id as keyof typeof expandedSections)}
                  className="flex items-center justify-between w-full text-left text-sm font-medium text-foreground hover:text-foreground"
                >
                  <span>{section.title}</span>
                  {expandedSections[section.id as keyof typeof expandedSections] ? (
                    <IconProvider iconstring="fa-chevron-down" size={16} />
                  ) : (
                    <IconProvider iconstring="fa-chevron-right" size={16} />
                  )}
                </button>

                {expandedSections[section.id as keyof typeof expandedSections] && (
                  <div className="mt-2 ml-4 space-y-1">
                    {section.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedSection(item.id)}
                        className={`block w-full text-left text-sm py-1 px-2 rounded text-foreground hover:text-foreground ${selectedSection === item.id
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            {selectedSection === 'profile' && <ProfileSection />}
            {selectedSection === 'environment-settings' && <EnvironmentSettingsSection />}
            {selectedSection === 'general-setup' && <GeneralSetupSection />}
            {selectedSection === 'games' && <GamesSetupSection />}
            {selectedSection === 'user-management' && <UserManagementSection />}
            {selectedSection === 'invitation-codes' && <InvitationCodesSection />}
            {selectedSection === 'permissions' && <PermissionsSection />}
            {selectedSection === 'status-management' && <StatusManagementSection />}
            {selectedSection === 'tutorial' && <TutorialManagementSection />}
            {selectedSection === 'database' && <DatabaseSection />}
            {selectedSection === 'sanity-check' && <SanityCheckSection />}
            {selectedSection === 'api-keys' && <ApiKeysSection />}
            {selectedSection === 'import-export' && <ImportExportSection />}
            {selectedSection === 'export-templates' && <ExportTemplatesSection />}

            {selectedSection !== 'profile' && selectedSection !== 'environment-settings' && selectedSection !== 'general-setup' && selectedSection !== 'games' && selectedSection !== 'user-management' && selectedSection !== 'invitation-codes' && selectedSection !== 'permissions' && selectedSection !== 'status-management' && selectedSection !== 'tutorial' && selectedSection !== 'database' && selectedSection !== 'sanity-check' && selectedSection !== 'api-keys' && selectedSection !== 'import-export' && selectedSection !== 'export-templates' && (
              <div className="text-center text-muted-foreground mt-20">
                <h2 className="text-xl font-medium mb-2">
                  {sidebarSections
                    .flatMap(section => section.items)
                    .find(item => item.id === selectedSection)?.label || 'Section'}
                </h2>
                <p>{t('platform.pages.settingspage.this_section_is_not_yet_implemented')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
